import { Hero } from "@/parts/hero";
import { IImageWithCaption, ILayout } from "@/utils/types";
import { motion } from "framer-motion";
import { BlogPosts } from "./blogPosts";
import { Cards } from "./cards";
import { Cta } from "./cta";
import { Faqs } from "./faqs";
import { Knowledge } from "./knowledge";
import { Media } from "./media";
import { Partners } from "./partners";
import { Process } from "./process";
import { QuestionFlow } from "./questionFlow";
import { Roadmap } from "./roadmap";
import { Sectors } from "./sectors";
import { Social } from "./social";
import { Split } from "./split";
import { Stats } from "./stats";
import { Team } from "./team";
import { Testimonials } from "./testimonials";
import { TextBlock } from "./textBlock";

export function Layout({
  data,
  backgroundColor,
  accentColor,
  location,
  date,
  endDate,
  timeOverride,
  thumbnail,
}: {
  data: ILayout;
  backgroundColor?: string;
  accentColor?: string;
  location?: string;
  date?: string;
  endDate?: string;
  timeOverride?: string;
  thumbnail?: IImageWithCaption;
}) {
  // Function to render part based on its _type, with explicit typing for 'part'
  const renderPart = (part: ILayout["parts"][number], i: number) => {
    // Determine the component to render based on part._type
    switch (part._type) {
      case "hero":
        return (
          <Hero
            {...part}
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            location={location}
            date={date}
            endDate={endDate}
            timeOverride={timeOverride}
            thumbnail={thumbnail}
            key={i}
          />
        );
      case "textBlock":
        return <TextBlock {...part} key={i} />;
      case "cards":
        return (
          <Cards
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      case "process":
        return (
          <Process
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      case "team":
        return (
          <Team
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      case "faqs":
        return (
          <Faqs
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      case "cta":
        return <Cta {...part} key={i} />;
      case "media":
        return <Media {...part} key={i} />;
      case "partners":
        return <Partners {...part} key={i} />;
      case "knowledge":
        return <Knowledge {...part} key={i} />;
      case "testimonials":
        return (
          <Testimonials
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      case "stats":
        return (
          <Stats
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      case "sectors":
        return (
          <Sectors
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      case "blogPosts":
        return <BlogPosts {...part} key={i} />;
      case "socialVideos":
        return <Social {...part} key={i} />;
      case "roadmap":
        return <Roadmap {...part} key={i} />;
      case "split":
        return <Split {...part} key={i} />;
      case "questionFlow":
        return (
          <QuestionFlow
            backgroundColor={backgroundColor}
            accentColor={accentColor}
            {...part}
            key={i}
          />
        );
      default:
        return <div key={i}>Not created a component yet for a type.</div>;
    }
  };

  // Parts flagged "Hide this section" in the CMS are kept but not rendered.
  const parts = (data?.parts ?? []).filter((part) => !part.hidden);

  return (
    <div className="flex flex-col gap-16 md:gap-24">
      {parts.map((part, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "0px 0px -80px 0px" }}
          transition={{ type: "spring", stiffness: 100, damping: 20, mass: 1 }}
          className={[
            part._type !== "hero" ? "pt-0" : "",
            i === parts.length - 1 && part._type !== "cta"
              ? "pb-16 md:pb-24"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {renderPart(part, i)}
        </motion.div>
      ))}
    </div>
  );
}
